/**
 * RendPleX Scenario Analyzer 2.0 — P3 Decision Intelligence.
 *
 * Tabbed container for P3A (Offer Price), P3B (Financing Strategies),
 * P3C (Constraint Engine) and P3D (Decision Summary). Only tabs that
 * have a fully-implemented deterministic backend are enabled; future
 * phases render a disabled "Coming soon" button so the intended
 * information architecture is visible from the first release.
 *
 * The container itself owns no financial state. Each tab is a pure
 * consumer of the current `scenario` prop and calls one Pro+-gated
 * canonical API. No DB writes, no LLM.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Compass } from 'lucide-react';
import OfferPriceTab from './decision/OfferPriceTab';
import FinancingStrategiesTab from './decision/FinancingStrategiesTab';
import ConstraintsTab from './decision/ConstraintsTab';
import DecisionSummaryTab from './decision/DecisionSummaryTab';

const TABS = [
  { key: 'offer_price', i18nKey: 'decision.tabOfferPrice', enabled: true },
  { key: 'financing',   i18nKey: 'decision.tabFinancing',  enabled: true },
  { key: 'constraints', i18nKey: 'decision.tabConstraints', enabled: true },
  { key: 'summary',     i18nKey: 'decision.tabSummary',    enabled: true },
];

const DecisionIntelligence = ({ scenario, onApply }) => {
  const { t } = useTranslation();
  const [active, setActive] = useState('offer_price');

  return (
    <section
      data-testid="decision-intelligence-section"
      className="mt-8 rounded-xl p-6 shadow-sm border border-indigo-200 dark:border-indigo-900 bg-gradient-to-br from-indigo-50/80 to-white dark:from-[#141a2e] dark:to-[#131824]"
    >
      <div className="flex items-start gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-indigo-500" />
            <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100">
              {t('decision.title')}
            </h2>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            {t('decision.subtitle')}
          </p>
        </div>
      </div>

      {/* Tab strip — scrollable on mobile. */}
      <div
        role="tablist"
        aria-label={t('decision.title')}
        className="flex gap-1 border-b border-slate-200 dark:border-slate-800 mb-4 overflow-x-auto -mx-1 px-1"
      >
        {TABS.map((tab) => {
          const isActive = active === tab.key && tab.enabled;
          const testId = `di-tab-${tab.key.replace('_', '-')}`;
          return (
            <button
              key={tab.key}
              role="tab"
              data-testid={testId}
              aria-selected={isActive}
              aria-controls={`di-panel-${tab.key}`}
              disabled={!tab.enabled}
              onClick={() => tab.enabled && setActive(tab.key)}
              className={`whitespace-nowrap px-3 sm:px-4 py-2 text-sm font-semibold rounded-t-md border-b-2 transition-colors ${
                isActive
                  ? 'text-indigo-700 dark:text-indigo-300 border-indigo-500 bg-white/50 dark:bg-slate-900/40'
                  : tab.enabled
                    ? 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-200'
                    : 'text-slate-400 dark:text-slate-600 border-transparent cursor-not-allowed'
              }`}
            >
              {t(tab.i18nKey)}
              {!tab.enabled && (
                <span className="ml-2 text-[10px] uppercase tracking-wider opacity-60">
                  {t('decision.comingSoon')}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Panels — only the active enabled tab renders. */}
      {active === 'offer_price' && (
        <div
          role="tabpanel"
          id="di-panel-offer_price"
          aria-labelledby="di-tab-offer-price"
        >
          <OfferPriceTab scenario={scenario} onApply={onApply} />
        </div>
      )}
      {active === 'financing' && (
        <div
          role="tabpanel"
          id="di-panel-financing"
          aria-labelledby="di-tab-financing"
        >
          <FinancingStrategiesTab scenario={scenario} onApply={onApply} />
        </div>
      )}
      {active === 'constraints' && (
        <div
          role="tabpanel"
          id="di-panel-constraints"
          aria-labelledby="di-tab-constraints"
        >
          <ConstraintsTab scenario={scenario} onApply={onApply} />
        </div>
      )}
      {active === 'summary' && (
        <div
          role="tabpanel"
          id="di-panel-summary"
          aria-labelledby="di-tab-summary"
        >
          <DecisionSummaryTab scenario={scenario} onApply={onApply} />
        </div>
      )}
    </section>
  );
};

export default DecisionIntelligence;
